import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  HomeIcon,
  ChevronRightIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  CheckIcon,
  TableCellsIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import { ApiService } from '../../services/api';

interface Suggestion {
  column_name: string;
  method: string;
  reasoning: string;
  confidence_score: number;
  missing_mechanism: string;
  missing_percentage: number;
  outlier_percentage: number;
}

interface UserApprovals {
  timestamp: string;
  actions: Record<string, 'accepted' | 'rejected'>;
  summary: {
    approved_count: number;
    rejected_count: number;
    total_suggestions: number;
  };
  approved_by: string;
  overall_status: string;
}

interface CorrectionRecommendationDetails {
  job_id: number;
  data_file_id: number;
  status: string;
  message: string;
  analysis_completed: boolean;
  analysis_started_at: string;
  analysis_ended_at: string;
  suggestions: Suggestion[];
  suggestions_count: number;
  analysis_results: any;
  user_approvals: UserApprovals | null;
  file_info: {
    data_file_id: number;
    data_file_name: string;
    data_file_path: string;
  };
}

interface SuggestionAction {
  column_name: string;
  action: 'accepted' | 'rejected' | '';
}

const CorrectionRecommendationEnhanced: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [correctionDetails, setCorrectionDetails] = useState<CorrectionRecommendationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suggestionActions, setSuggestionActions] = useState<SuggestionAction[]>([]);
  const [approving, setApproving] = useState(false);
  const [approved, setApproved] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [stageStatus, setStageStatus] = useState<string | null>(null);

  useEffect(() => {
    const fetchCorrectionDetails = async () => {
      if (!jobId) {
        setError('Job ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch both correction details and job details to get stage status
        const [details, jobDetails] = await Promise.all([
          ApiService.getCorrectionRecommendationDetails(parseInt(jobId)),
          ApiService.getJobById(parseInt(jobId), true)
        ]);
        
        setCorrectionDetails(details);
        
        // Get the correction_recommendation stage status
        const correctionStage = jobDetails.stages?.find(
          stage => stage.stage_name === 'correction_recommendation'
        );
        if (correctionStage) {
          setStageStatus(correctionStage.status);
          // Set initial state based on stage status
          if (correctionStage.status === 'APPROVED') {
            setApproved(true);
          } else if (correctionStage.status === 'REJECTED') {
            setRejected(true);
          }
        }
        
        // Initialize suggestion actions - pre-populate from stored user approvals if available
        if (details.suggestions) {
          const initialActions: SuggestionAction[] = details.suggestions.map((suggestion: Suggestion) => {
            // Check if there are stored user approvals for this column
            let action: 'accepted' | 'rejected' | '' = '';
            if (details.user_approvals && details.user_approvals.actions) {
              const storedAction = details.user_approvals.actions[suggestion.column_name];
              if (storedAction === 'accepted' || storedAction === 'rejected') {
                action = storedAction;
              }
            }
            
            return {
              column_name: suggestion.column_name,
              action: action
            };
          });
          setSuggestionActions(initialActions);
        }
        
        setError(null);
      } catch (err) {
        setError('Failed to fetch correction recommendation details');
        console.error('Error fetching correction recommendation details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCorrectionDetails();
  }, [jobId]);

  const handleActionChange = (columnName: string, action: 'accepted' | 'rejected' | '') => {
    setSuggestionActions(prev => 
      prev.map(item => 
        item.column_name === columnName 
          ? { ...item, action } 
          : item
      )
    );
  };

  const handleApprove = async () => {
    if (!correctionDetails) return;

    setApproving(true);
    try {
      if (!jobId) {
        throw new Error('Job ID is required');
      }
      
      // Convert suggestionActions array to Record format expected by API
      const actionsRecord: Record<string, 'accepted' | 'rejected'> = {};
      suggestionActions.forEach(action => {
        if (action.action === 'accepted' || action.action === 'rejected') {
          actionsRecord[action.column_name] = action.action;
        }
      });
      
      // Call the real API to save approval decisions
      const approvalData = {
        actions: actionsRecord,
        timestamp: new Date().toISOString(),
        approved_by: 'user' // You could get this from user context
      };
      
      const result = await ApiService.approveCorrectionRecommendations(parseInt(jobId), approvalData);
      
      console.log('Approval data saved:', result);
      
      setApproved(true);
      alert(`Approval saved successfully! ${result.approved_count} approved, ${result.rejected_count} rejected.`);
    } catch (error) {
      console.error('Failed to save approval:', error);
      alert('Failed to save approval. Please try again.');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!correctionDetails) return;

    setRejecting(true);
    try {
      if (!jobId) {
        throw new Error('Job ID is required');
      }
      
      // Convert suggestionActions array to Record format expected by API
      const actionsRecord: Record<string, 'accepted' | 'rejected'> = {};
      suggestionActions.forEach(action => {
        if (action.action === 'accepted' || action.action === 'rejected') {
          actionsRecord[action.column_name] = action.action;
        }
      });
      
      // Call the real API to save rejection decisions
      const rejectionData = {
        actions: actionsRecord,
        timestamp: new Date().toISOString(),
        approved_by: 'user' // You could get this from user context
      };
      
      const result = await ApiService.rejectCorrectionRecommendations(parseInt(jobId), rejectionData);
      
      console.log('Rejection data saved:', result);
      
      setRejected(true);
      alert(`Rejection saved successfully! ${result.approved_count} approved, ${result.rejected_count} rejected.`);
    } catch (error) {
      console.error('Failed to save rejection:', error);
      alert('Failed to save rejection. Please try again.');
    } finally {
      setRejecting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'in_progress':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      default:
        return <ExclamationCircleIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600 bg-green-100';
    if (score >= 0.6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getActionButton = (suggestion: Suggestion) => {
    const currentAction = suggestionActions.find(a => a.column_name === suggestion.column_name)?.action || '';
    
    if (suggestion.method === 'No action needed') {
      return (
        <div className="text-sm text-gray-500 italic">
          No action required
        </div>
      );
    }

    return (
      <div className="flex space-x-2">
        <button
          onClick={() => handleActionChange(suggestion.column_name, 'accepted')}
          className={`flex items-center px-3 py-1 rounded-md text-sm font-medium transition-colors ${
            currentAction === 'accepted'
              ? 'bg-green-100 text-green-800 border border-green-300'
              : 'bg-gray-100 text-gray-600 hover:bg-green-50 hover:text-green-700'
          }`}
        >
          <HandThumbUpIcon className="w-4 h-4 mr-1" />
          Accept
        </button>
        <button
          onClick={() => handleActionChange(suggestion.column_name, 'rejected')}
          className={`flex items-center px-3 py-1 rounded-md text-sm font-medium transition-colors ${
            currentAction === 'rejected'
              ? 'bg-red-100 text-red-800 border border-red-300'
              : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-700'
          }`}
        >
          <HandThumbDownIcon className="w-4 h-4 mr-1" />
          Reject
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-lg text-gray-600">Loading correction recommendations...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !correctionDetails) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">
                  {error || 'Failed to load correction recommendation details'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm text-gray-500 flex-1 min-w-0">
              <HomeIcon className="w-4 h-4 flex-shrink-0" />
              <ChevronRightIcon className="w-4 h-4 flex-shrink-0" />
              <button 
                onClick={() => navigate('/job-monitoring')}
                className="hover:text-gray-700 transition-colors flex-shrink-0"
              >
                Workflow Monitoring
              </button>
              <ChevronRightIcon className="w-4 h-4 flex-shrink-0" />
              <span className="text-gray-900 font-medium truncate">
                Workflow #{correctionDetails.job_id} - Correction Recommendations
              </span>
            </nav>

            {/* Back Button */}
            <button
              onClick={() => navigate('/job-monitoring')}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0 ml-4"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Workflows</span>
              <span className="sm:hidden">Back</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Status Overview */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <DocumentTextIcon className="w-5 h-5 mr-2" />
              Correction Recommendations Overview
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center">
                  {getStatusIcon(correctionDetails.status)}
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Status</p>
                    <p className="text-sm text-gray-600">{correctionDetails.status}</p>
                  </div>
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center">
                  <TableCellsIcon className="w-5 h-5 text-green-500" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Total Columns</p>
                    <p className="text-sm text-gray-600">{correctionDetails.suggestions_count}</p>
                  </div>
                </div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">Needs Action</p>
                    <p className="text-sm text-gray-600">
                      {correctionDetails.suggestions.filter(s => s.method !== 'No action needed').length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center">
                  <CheckIcon className="w-5 h-5 text-purple-500" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">File</p>
                    <p className="text-sm text-gray-600 truncate">{correctionDetails.file_info.data_file_name}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* User Approval Status Indicator */}
        {correctionDetails.user_approvals && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <CheckCircleIcon className="w-5 h-5 text-blue-500 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-blue-900">
                  Previous Decisions Loaded
                </h3>
                <p className="text-sm text-blue-700 mt-1">
                  Your previous approval decisions from {new Date(correctionDetails.user_approvals.timestamp).toLocaleString()} have been loaded.
                </p>
                <div className="mt-2 text-xs text-blue-600">
                  Previously: {correctionDetails.user_approvals.summary.approved_count} accepted, {correctionDetails.user_approvals.summary.rejected_count} rejected
                  {correctionDetails.user_approvals.approved_by && ` by ${correctionDetails.user_approvals.approved_by}`}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Column-wise Recommendations */}
        <div className="space-y-6">
          {correctionDetails.suggestions.map((suggestion, index) => (
            <div key={suggestion.column_name} className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Column: {suggestion.column_name}
                </h3>
              </div>
              <div className="p-6 relative">
                {/* Column Details - Full Width */}
                <div className="space-y-4 pr-40">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Confidence Score</label>
                      <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(suggestion.confidence_score)}`}>
                        {(suggestion.confidence_score * 100).toFixed(1)}%
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Missing Mechanism</label>
                      <p className="text-sm text-gray-900">{suggestion.missing_mechanism}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Missing Percentage</label>
                      <p className="text-sm text-gray-900">{suggestion.missing_percentage.toFixed(2)}%</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700">Outlier Percentage</label>
                      <p className="text-sm text-gray-900">{suggestion.outlier_percentage.toFixed(2)}%</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Recommended Method</label>
                    <p className="text-sm text-gray-900 font-medium">{suggestion.method}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Reasoning</label>
                    <p className="text-sm text-gray-600">{suggestion.reasoning}</p>
                  </div>
                </div>

                {/* Action Controls - Bottom Right Corner */}
                <div className="absolute bottom-6 right-6">
                  <div className="text-right">
                    <label className="text-sm font-medium text-gray-700 block mb-2">Action</label>
                    {getActionButton(suggestion)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary Table */}
        <div className="bg-white rounded-lg shadow mt-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Summary Table</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Column Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confidence Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Missing Mechanism
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Missing %
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Outlier %
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reasoning
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {correctionDetails.suggestions.map((suggestion) => {
                  const action = suggestionActions.find(a => a.column_name === suggestion.column_name)?.action || '';
                  return (
                    <tr key={suggestion.column_name}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {suggestion.column_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getConfidenceColor(suggestion.confidence_score)}`}>
                          {(suggestion.confidence_score * 100).toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {suggestion.missing_mechanism}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {suggestion.missing_percentage.toFixed(2)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {suggestion.outlier_percentage.toFixed(2)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {suggestion.method}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate" title={suggestion.reasoning}>
                        {suggestion.reasoning}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {action ? (
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            action === 'accepted' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {action.charAt(0).toUpperCase() + action.slice(1)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Approval Section */}
        <div className="bg-white rounded-lg shadow mt-8">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Review Complete</h3>
                <p className="text-sm text-gray-600">
                  Review all recommendations and choose to approve or reject your decisions.
                </p>
              </div>
              <div>
                {approved || stageStatus === 'APPROVED' ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircleIcon className="w-5 h-5 mr-2" />
                    <span className="font-medium">Approved</span>
                  </div>
                ) : rejected || stageStatus === 'REJECTED' ? (
                  <div className="flex items-center text-red-600">
                    <XCircleIcon className="w-5 h-5 mr-2" />
                    <span className="font-medium">Rejected</span>
                  </div>
                ) : (
                  <div className="flex space-x-3">
                    <button
                      onClick={handleApprove}
                      disabled={approving || rejecting || stageStatus === 'APPROVED' || stageStatus === 'REJECTED'}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {approving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Approving...
                        </>
                      ) : (
                        <>
                          <CheckIcon className="w-5 h-5 mr-2" />
                          Approve
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleReject}
                      disabled={approving || rejecting || stageStatus === 'APPROVED' || stageStatus === 'REJECTED'}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {rejecting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Rejecting...
                        </>
                      ) : (
                        <>
                          <XCircleIcon className="w-5 h-5 mr-2" />
                          Reject
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorrectionRecommendationEnhanced;
